/**
 * Equipment Tracker - Assign Equipment
 * @author Omid Zaeri (zaerio)
 */

import React, { useState, useEffect } from 'react';
import {
  Form, FormField, Input, Select, Button, Header, Container, SpaceBetween, Textarea,
} from '@cloudscape-design/components';
import { createEquipment, getUsers } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AssignEquipment() {
  const { isAdmin, isImpersonating, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ assignedTo: '', type: '', description: '', serialNumber: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isAdmin) {
      const viewAs = isImpersonating ? currentUser?.alias : undefined;
      getUsers(undefined, viewAs).then(setUsers).catch(console.error);
    }
  }, [isAdmin, isImpersonating, currentUser?.alias]);

  if (!isAdmin) return <Container><p>Admin access required.</p></Container>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createEquipment(form);
      setMessage('Equipment assigned successfully. AUP email sent.');
      setForm({ assignedTo: '', type: '', description: '', serialNumber: '' });
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container header={<Header variant="h1">Assign Equipment</Header>}>
      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button href="/" variant="link">Cancel</Button>
              <Button variant="primary" formAction="submit" loading={submitting}>Assign</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Assign To">
              <Select
                selectedOption={form.assignedTo ? { value: form.assignedTo, label: form.assignedTo } : null}
                onChange={({ detail }) => setForm({ ...form, assignedTo: detail.selectedOption.value })}
                options={users.map((u) => ({ value: u.alias, label: `${u.name} (${u.alias}) - ${u.tier}` }))}
                placeholder="Select team member"
              />
            </FormField>
            <FormField label="Equipment Type">
              <Input value={form.type} onChange={({ detail }) => setForm({ ...form, type: detail.value })} placeholder="e.g., Laptop, Monitor, Phone" />
            </FormField>
            <FormField label="Serial Number">
              <Input value={form.serialNumber} onChange={({ detail }) => setForm({ ...form, serialNumber: detail.value })} />
            </FormField>
            <FormField label="Description">
              <Textarea value={form.description} onChange={({ detail }) => setForm({ ...form, description: detail.value })} />
            </FormField>
            {message && <p>{message}</p>}
          </SpaceBetween>
        </Form>
      </form>
    </Container>
  );
}
